// frontend/src/components/settings/planningDefaults.js
//
// The store-level planning defaults as one slice: the five fields that root
// the inheritance chains (inventory target pair, lead time, order cadence
// pair — backend/api/services/planning_defaults.py), the rule that says
// whether a form holds values the endpoint accepts, and the conversion of
// that form into the `planning` block the endpoint reads. Store settings and
// the install setup step both render the slice through PlanningDefaultsFields
// and send it through here, so neither can drift from the other.
//
// Under Safety stock the days-of-supply control is hidden and its value is
// never sent: the endpoint refuses `target_days_of_supply` under an effective
// Safety stock (authentication/services/planning_settings.py), and the stored
// value stays for a flip back to Days of supply. An edit made before the flip
// stays in the form, unchecked and unsent, and shows again after it.

import { isValidTargetDaysOfSupply, readsDaysOfSupply } from '../../utils/inventoryTargetMode';
import { isValidLeadTimeDays } from '../../utils/leadTime';

const sendsDays = (form) => readsDaysOfSupply(form.inventory_target_mode);

/**
 * Whether the typed fields the form sends hold values the endpoint accepts.
 * The pickers cannot hold a refused value; days of supply and lead time are
 * held as the digits the control shows, and `core.models.*_is_valid` accepts
 * only a whole number inside its bounds.
 */
export const planningDefaultsValid = (form) =>
  (!sendsDays(form) || isValidTargetDaysOfSupply(String(form.target_days_of_supply)))
  && isValidLeadTimeDays(String(form.lead_time_days));

/**
 * The slice as the endpoint reads it: the digits become numbers, because the
 * validator refuses '45' as well as 45.0 — a form value sent verbatim would
 * 400 on a field the merchant filled in correctly. Callers gate on
 * `planningDefaultsValid` first, so Number() only ever sees a whole number.
 */
export const planningDefaultsPayload = (form) => ({
  inventory_target_mode: form.inventory_target_mode,
  ...(sendsDays(form) && { target_days_of_supply: Number(form.target_days_of_supply) }),
  lead_time_days: Number(form.lead_time_days),
  review_frequency: form.review_frequency,
  preferred_order_day: form.preferred_order_day,
});

/**
 * Whether `form` differs from `saved` only by a days edit the form hides —
 * one that is never sent, so it is no change to save.
 */
export const onlyHiddenDaysEdited = (form, saved) =>
  !sendsDays(form)
  && String(form.target_days_of_supply) !== String(saved.target_days_of_supply)
  && Object.keys(saved).every(
    (field) => field === 'target_days_of_supply' || form[field] === saved[field],
  );
